/* eslint-disable react/jsx-no-bind */
import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Fab,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Rnd } from 'react-rnd';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SpaIcon from '@mui/icons-material/Spa';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenWithIcon from '@mui/icons-material/OpenWith';

// ── Config ────────────────────────────────────────────────────────────────────
const AI_API = 'https://jacooo-pgc-ai-chatbot.hf.space';
const MAX_HISTORY = 12;

// ── Helpers ───────────────────────────────────────────────────────────────────
function getSensorsFromStorage() {
  try {
    const raw = localStorage.getItem('pgc_sensors');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function buildSensorContextPayload() {
  const s = getSensorsFromStorage();
  // Only send live context when we have valid non-null readings
  if (
    !s ||
    s.temperature == null ||
    s.humidity == null ||
    s.intensity == null
  ) {
    return null;
  }
  return {
    temp: s.temperature,
    rh: s.humidity,
    light: s.intensity,
    is_live: true,
  };
}

function DataSourceBadge({ source }) {
  if (!source) return null;
  const sl = source.toLowerCase();
  if (sl.includes('local verified') || sl.includes('verified database')) {
    return (
      <Chip
        label="✓ Verified Database"
        size="small"
        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem', height: 20, mb: 0.5 }}
      />
    );
  }
  if (sl.includes('llm') || sl.includes('ai generated') || sl.includes('qualitative') || sl.includes('ai knowledge')) {
    return (
      <Chip
        label="⚠ AI Generated"
        size="small"
        sx={{ bgcolor: '#fff8e1', color: '#f57f17', fontSize: '0.65rem', height: 20, mb: 0.5 }}
      />
    );
  }
  return null;
}

function Message({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 1.5,
      }}
    >
      {!isUser && (
        <Box
          sx={{
            width: 28, height: 28, borderRadius: '50%', bgcolor: '#2e7d32',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mr: 1, mt: 0.5, flexShrink: 0,
          }}
        >
          <SpaIcon sx={{ fontSize: 16, color: 'white' }} />
        </Box>
      )}
      <Box sx={{ maxWidth: '80%' }}>
        {!isUser && msg.dataSource && <DataSourceBadge source={msg.dataSource} />}
        <Box
          sx={{
            bgcolor: isUser ? '#1b5e20' : '#f5f5f5',
            color: isUser ? 'white' : '#212121',
            borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            px: 1.5, py: 1,
            fontSize: '0.82rem',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {msg.content}
        </Box>
      </Box>
    </Box>
  );
}

function TypingBubble() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
      <Box
        sx={{
          width: 28, height: 28, borderRadius: '50%', bgcolor: '#2e7d32',
          display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1,
        }}
      >
        <SpaIcon sx={{ fontSize: 16, color: 'white' }} />
      </Box>
      <Box sx={{ bgcolor: '#f5f5f5', borderRadius: '18px 18px 18px 4px', px: 2, py: 1.2 }}>
        <Box sx={{ display: 'flex', gap: 0.6 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 7, height: 7, borderRadius: '50%', bgcolor: '#9e9e9e',
                animation: 'bounce 1.2s infinite',
                animationDelay: `${i * 0.2}s`,
                '@keyframes bounce': {
                  '0%, 80%, 100%': { transform: 'scale(0.8)', opacity: 0.5 },
                  '40%': { transform: 'scale(1)', opacity: 1 },
                },
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ── Default position: bottom-right, just above the FAB ────────────────────────
const DEFAULT_W = 360;
const DEFAULT_H = 500;
function getDefaultPos() {
  if (typeof window === 'undefined') return { x: 0, y: 0 };
  return {
    x: Math.max(0, window.innerWidth - DEFAULT_W - 24),
    y: Math.max(0, window.innerHeight - DEFAULT_H - 90),
  };
}

// ── Main Widget ───────────────────────────────────────────────────────────────
export default function AIChatWidget() {
  const [open, setOpen] = useState(false);

  // Tour integration: listen for custom events so AgronomistTour can control open state
  useEffect(() => {
    const handleTourStart = () => setOpen(false);
    const handleTourOpenWidget = () => setOpen(true);
    window.addEventListener('pgc-tour-start', handleTourStart);
    window.addEventListener('pgc-tour-open-widget', handleTourOpenWidget);
    return () => {
      window.removeEventListener('pgc-tour-start', handleTourStart);
      window.removeEventListener('pgc-tour-open-widget', handleTourOpenWidget);
    };
  }, []);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "🌱 Hi! I'm your PGC Agronomist AI. Tell me what plant you're growing and I'll check if your chamber conditions are optimal!",
      dataSource: null,
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesContainerRef = useRef(null);
  // Track "was near bottom" BEFORE each render so large incoming messages don't
  // incorrectly break auto-scroll (post-render scrollHeight is larger).
  const wasStickyRef = useRef(true);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    wasStickyRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  // Persist size/position across open-close cycles
  const [rndSize, setRndSize] = useState({ width: DEFAULT_W, height: DEFAULT_H });
  const [rndPos, setRndPos] = useState(null); // null = use getDefaultPos() on first open

  // Scroll to bottom only when user was near bottom before this update.
  // Opening the widget never forces a scroll.
  const scrollToBottomIfSticky = useCallback(() => {
    if (!wasStickyRef.current) return;
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottomIfSticky();
  }, [messages, loading, scrollToBottomIfSticky]);

  const historyForApi = messages
    .slice(-MAX_HISTORY)
    .map((m) => ({ role: m.role === 'assistant' ? 'assistant' : 'user', content: m.content }));

  async function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setLoading(true);

    try {
      const sensorContext = buildSensorContextPayload();
      const res = await fetch(`${AI_API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: trimmed,
          history: historyForApi,
          sensor_context: sensorContext,
        }),
      });
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.response || 'Sorry, I could not generate a response.',
          dataSource: data.data_source || null,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '⚠️ Could not connect to the AI service. Please try again shortly.', dataSource: null },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const QUICK_PROMPTS = [
    "I'm growing lettuce, are conditions okay?",
    'cabe stage vegetatif, kondisi gimana?',
    'Is my chamber humidity okay for tomatoes?',
  ];

  // Named handlers
  const handleInputChange = (e) => setInput(e.target.value);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleQuickPrompt = (e) => setInput(e.currentTarget.dataset.prompt);
  const handleDragStop = (e, d) => setRndPos({ x: d.x, y: d.y });
  const handleResizeStop = (e, direction, ref, delta, pos) => {
    setRndSize({ width: ref.offsetWidth, height: ref.offsetHeight });
    setRndPos(pos);
  };

  // Sensor label for header
  const sensors = getSensorsFromStorage();
  const sensorsLabel = sensors?.temperature
    ? `🌡️ ${sensors.temperature}°C · 💧${sensors.humidity}% · ☀️${sensors.intensity} lux`
    : 'No sensor data (visit Dashboard to sync)';

  const currentPos = rndPos || getDefaultPos();

  return (
    <>
      {/* ── Draggable + Resizable Chat Panel ── */}
      {open && (
        <Rnd
          size={rndSize}
          position={currentPos}
          onDragStop={handleDragStop}
          onResizeStop={handleResizeStop}
          minWidth={280}
          minHeight={320}
          maxWidth={700}
          maxHeight={750}
          bounds="window"
          dragHandleClassName="pgc-chat-drag-handle"
          style={{ zIndex: 1300 }}
          enableResizing={{
            top: true, right: true, bottom: true, left: true,
            topRight: true, bottomRight: true, bottomLeft: true, topLeft: true,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid #c8e6c9',
            }}
          >
            {/* ── Header (drag zone) ── */}
            <Box
              className="pgc-chat-drag-handle"
              sx={{
                bgcolor: '#1b5e20',
                px: 2, py: 1.2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'move',
                userSelect: 'none',
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpaIcon sx={{ color: '#a5d6a7', fontSize: 20 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
                    PGC Agronomist AI
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#a5d6a7', fontSize: '0.65rem' }}>
                    {sensorsLabel}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* Drag hint icon */}
                <DragIndicatorIcon sx={{ color: '#a5d6a7', fontSize: 18, opacity: 0.7 }} />
                <IconButton
                  size="small"
                  onClick={handleClose}
                  onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); handleClose(); }}
                  sx={{ color: 'white', touchAction: 'none', pointerEvents: 'auto' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* ── Messages ── */}
            <Box ref={messagesContainerRef} onScroll={handleMessagesScroll} id="pgc-chat-messages" sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: '#fafafa' }}>
              {messages.map((msg, i) => (
                <Message key={i} msg={msg} />
              ))}
              {loading && <TypingBubble />}
            </Box>

            {/* ── Quick Prompts ── */}
            {messages.length === 1 && (
              <Box sx={{ px: 1.5, pb: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, bgcolor: '#fafafa', flexShrink: 0 }}>
                {QUICK_PROMPTS.map((qp) => (
                  <Chip
                    key={qp}
                    label={qp}
                    size="small"
                    variant="outlined"
                    clickable
                    data-prompt={qp}
                    onClick={handleQuickPrompt}
                    sx={{ fontSize: '0.68rem', cursor: 'pointer', borderColor: '#a5d6a7', color: '#2e7d32' }}
                  />
                ))}
              </Box>
            )}

            {/* ── Resize hint caption ── */}
            <Box sx={{ px: 1.5, pt: 0.3, pb: 0, bgcolor: '#fafafa', flexShrink: 0 }}>
              <Typography variant="caption" sx={{ color: '#bdbdbd', fontSize: '0.6rem', display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <OpenWithIcon sx={{ fontSize: 10 }} /> Drag header to move · Drag edges to resize
              </Typography>
            </Box>

            {/* ── Input ── */}
            <Box id="pgc-chat-input-area" sx={{ p: 1.5, bgcolor: 'white', borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1, flexShrink: 0 }}>
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={3}
                placeholder="Ask about your plants..."
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={loading}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, fontSize: '0.82rem' } }}
              />
              <IconButton
                onClick={handleSend}
                disabled={loading || !input.trim()}
                sx={{ bgcolor: '#2e7d32', color: 'white', '&:hover': { bgcolor: '#1b5e20' }, width: 40, height: 40, alignSelf: 'flex-end' }}
              >
                {loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SendIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Paper>
        </Rnd>
      )}

      {/* ── Floating Button (only when panel is closed) ── */}
      {!open && (
        <Fab
          id="pgc-chat-fab"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            bgcolor: '#2e7d32',
            color: 'white',
            zIndex: 1301,
            '&:hover': { bgcolor: '#1b5e20' },
            boxShadow: '0 4px 20px rgba(46,125,50,0.4)',
          }}
        >
          <SmartToyIcon />
        </Fab>
      )}
    </>
  );
}
